import React from "react";
import { Box, Typography, Avatar, styled, useTheme } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";
import { BookingData } from "../../../../types/bookings";
import { BookingType } from "../AddBookingV2";

interface BookingPreviewSidebarProps {
  bookingData: BookingData;
  bookingType: BookingType;
}

const SkeletonBar = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  height: 14,
  marginBottom: theme.spacing(1),
}));

const BookingPreviewSidebar: React.FC<BookingPreviewSidebarProps> = ({
  bookingData,
  bookingType,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const isDarkMode = theme.palette.mode === "dark";

  const skeletonBg = isDarkMode ? `${colors.primary[400]}20` : "#F0F2F5";
  const avatarBg = isDarkMode ? `${colors.primary[400]}30` : "#EDF2F7";
  const avatarIconColor = isDarkMode ? colors.primary[300] : "#4A5568";
  const textMain = isDarkMode ? "#ffffff" : "#1A202C";
  const textMuted = isDarkMode ? colors.primary[300] : "#718096";
  const borderLine = isDarkMode
    ? `1px dashed ${colors.primary[300]}30`
    : "1px dashed #E2E8F0";

  const clientName =
    bookingData.first_name || bookingData.surname
      ? `${bookingData.client_salutation || ""} ${bookingData.first_name || ""} ${bookingData.surname || ""}`.trim()
      : null;

  const companyName = bookingData.company_name || null;

  const deceasedName =
    bookingData.deceased_name || bookingData.deceased_surname
      ? `${bookingData.deceased_salutation || ""} ${bookingData.deceased_name || ""} ${bookingData.deceased_surname || ""}`.trim()
      : null;

  const serviceDate =
    bookingData.date_of_service || bookingData.start_date_of_event || null;
  const venueName = bookingData.venue_name || null;
  const city = bookingData.city || null;

  const formatDate = (raw: string | null | undefined): string => {
    if (!raw) return "";
    const [year, month, day] = raw.split("-");
    if (!year || !month || !day) return raw;
    return `${day}/${month}/${year}`;
  };

  const Skeleton = ({ width = "70%" }: { width?: string }) => (
    <SkeletonBar sx={{ width, backgroundColor: skeletonBg }} />
  );

  const contacts = bookingData.contacts || [];

  const renderContacts = () => {
    if (!contacts.length) return null;
    return (
      <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
        <SectionLabel>Contacts</SectionLabel>
        {contacts.map((c, i) => {
          const name =
            c.contact?.name ||
            `${c.contact?.first_name || ""} ${c.contact?.last_name || ""}`.trim() ||
            "Unnamed contact";
          return (
            <Box
              key={c.contact_id || i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1.25,
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: textMain, fontSize: "13px", fontWeight: 600 }}
                >
                  {name}
                </Typography>
                {c.contact?.email && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: textMuted,
                      fontSize: "11px",
                      display: "block",
                    }}
                  >
                    {c.contact.email}
                  </Typography>
                )}
                {c.contact?.phone && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: textMuted,
                      fontSize: "11px",
                      display: "block",
                    }}
                  >
                    {c.contact.phone}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: textMuted,
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  backgroundColor: skeletonBg,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {(c.role || "").replace(/_/g, " ")}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };
  const Row = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
      <Typography variant="body2" sx={{ color: textMuted, fontSize: "12px" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: textMain, fontSize: "12px", fontWeight: 600 }}
      >
        {value}
      </Typography>
    </Box>
  );

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
      variant="caption"
      sx={{
        color: textMuted,
        fontSize: "11px",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        display: "block",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );

  const renderClient = () => {
    const displayName =
      bookingType === "funeral_home" ? companyName : clientName;
    const email = bookingData.email;
    const phone = bookingData.phone;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          pb: 3,
          borderBottom: borderLine,
          mb: 3,
        }}
      >
        <Avatar
          sx={{
            width: 56,
            height: 56,
            backgroundColor: avatarBg,
            color: avatarIconColor,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 28 }} />
        </Avatar>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: textMuted,
              fontSize: "11px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {bookingType === "funeral_home" ? "Company" : "Client"}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: displayName ? textMain : textMuted,
              fontSize: "15px",
              lineHeight: 1.3,
            }}
          >
            {displayName || ""}
          </Typography>
          {email && (
            <Typography
              variant="body2"
              sx={{ color: textMuted, fontSize: "12px" }}
            >
              {email}
            </Typography>
          )}
          {phone && (
            <Typography
              variant="body2"
              sx={{ color: textMuted, fontSize: "12px" }}
            >
              {phone}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderMiddle = () => {
    switch (bookingType) {
      case "funeral":
      case "funeral_home":
        return (
          <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
            <SectionLabel>Deceased</SectionLabel>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Photo box — shows uploaded image or fallback icon */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  backgroundColor: avatarBg,
                  borderRadius: 16,
                  flexShrink: 0,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {bookingData.photo_url ? (
                  <Box
                    component="img"
                    src={bookingData.photo_url}
                    alt="Deceased"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <PersonOutlineIcon
                    sx={{ fontSize: 28, color: avatarIconColor }}
                  />
                )}
              </Box>

              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: deceasedName ? textMain : textMuted,
                    fontSize: "14px",
                    mb: 0.25,
                  }}
                >
                  {deceasedName || ""}
                </Typography>
                {bookingData.deceased_dob || bookingData.deceased_dod ? (
                  <Typography
                    variant="body2"
                    sx={{ color: textMuted, fontSize: "12px", mb: 0.25 }}
                  >
                    {formatDate(bookingData.deceased_dob)} –{" "}
                    {formatDate(bookingData.deceased_dod)}
                  </Typography>
                ) : (
                  <Skeleton width="80%" />
                )}
                {bookingData.gender ? (
                  <Typography
                    variant="body2"
                    sx={{ color: textMuted, fontSize: "12px" }}
                  >
                    {bookingData.gender}
                    {bookingData.religion ? ` · ${bookingData.religion}` : ""}
                    {bookingData.type_of_service
                      ? ` · ${bookingData.type_of_service}`
                      : ""}
                  </Typography>
                ) : (
                  <Skeleton width="50%" />
                )}
              </Box>
            </Box>
          </Box>
        );

      case "conference":
        return (
          <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
            <SectionLabel>Conference</SectionLabel>
            {bookingData.title ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: textMain,
                    fontSize: "14px",
                    mb: 0.5,
                  }}
                >
                  {bookingData.conference_type
                    ? `${bookingData.conference_type}: `
                    : ""}
                  {bookingData.title}
                </Typography>
                {bookingData.sub_title && (
                  <Typography
                    variant="body2"
                    sx={{ color: textMuted, fontSize: "12px", mb: 1 }}
                  >
                    {bookingData.sub_title}
                  </Typography>
                )}
                {bookingData.hosts && (
                  <Row label="Host" value={bookingData.hosts} />
                )}
                {bookingData.guests && (
                  <Row label="Guest" value={bookingData.guests} />
                )}
                {bookingData.expected_audience_size && (
                  <Row
                    label="Audience"
                    value={String(bookingData.expected_audience_size)}
                  />
                )}
              </>
            ) : (
              <>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
                <Skeleton width="75%" />
              </>
            )}
          </Box>
        );

      case "church":
        return (
          <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
            <SectionLabel>Church</SectionLabel>
            {bookingData.church_name ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: textMain,
                    fontSize: "14px",
                    mb: 0.5,
                  }}
                >
                  {bookingData.church_name}
                </Typography>
                {bookingData.title && (
                  <Row label="Title" value={bookingData.title} />
                )}
                {bookingData.leader_preacher_name && (
                  <Row
                    label="Leader"
                    value={`${bookingData.leader_preacher_name} ${bookingData.leader_preacher_surname || ""}`.trim()}
                  />
                )}
                {bookingData.preacher_name && (
                  <Row
                    label="Preacher"
                    value={`${bookingData.preacher_name} ${bookingData.preacher_surname || ""}`.trim()}
                  />
                )}
              </>
            ) : (
              <>
                <Skeleton width="60%" />
                <Skeleton width="45%" />
              </>
            )}
          </Box>
        );

      case "event":
        return (
          <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
            <SectionLabel>Event</SectionLabel>
            {bookingData.event_name || bookingData.title ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: textMain,
                    fontSize: "14px",
                    mb: 0.5,
                  }}
                >
                  {bookingData.event_name || bookingData.title}
                </Typography>
                {bookingData.sub_title && (
                  <Typography
                    variant="body2"
                    sx={{ color: textMuted, fontSize: "12px", mb: 1 }}
                  >
                    {bookingData.sub_title}
                  </Typography>
                )}
                {bookingData.hosts && (
                  <Row label="Host" value={bookingData.hosts} />
                )}
                {bookingData.guests && (
                  <Row label="Guest" value={bookingData.guests} />
                )}
              </>
            ) : (
              <>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </>
            )}
          </Box>
        );

      case "other":
        return (
          <Box sx={{ pb: 3, borderBottom: borderLine, mb: 3 }}>
            <SectionLabel>Content</SectionLabel>
            {bookingData.title ? (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: textMain,
                    fontSize: "14px",
                    mb: 0.5,
                  }}
                >
                  {bookingData.content_type
                    ? `${bookingData.content_type}: `
                    : ""}
                  {bookingData.title}
                </Typography>
                {bookingData.hosts && (
                  <Row label="Host" value={bookingData.hosts} />
                )}
                {bookingData.guests && (
                  <Row label="Guest" value={bookingData.guests} />
                )}
              </>
            ) : (
              <>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const renderService = () => (
    <Box>
      <SectionLabel>Service</SectionLabel>
      {serviceDate || venueName || city ? (
        <>
          {serviceDate && <Row label="Date" value={formatDate(serviceDate)} />}
          {(bookingData.start_time || bookingData.end_time) && (
            <Row
              label="Time"
              value={`${bookingData.start_time || ""} – ${bookingData.end_time || ""}`}
            />
          )}
          {venueName && <Row label="Venue" value={venueName} />}
          {city && <Row label="City" value={city} />}
        </>
      ) : (
        <>
          <Skeleton width="60%" />
          <Skeleton width="80%" />
          <Skeleton width="45%" />
        </>
      )}
    </Box>
  );

  const renderPackage = () => {
    if (!bookingData.package) return null;
    return (
      <Box sx={{ pt: 3, borderTop: borderLine, mt: 3 }}>
        <SectionLabel>Package</SectionLabel>
        <Row
          label={bookingData.package.name || "Package"}
          value={`$${(bookingData.total || 0).toFixed(2)}`}
        />
        {(bookingData.additional_services_total ?? 0) > 0 && (
          <Row
            label="Add-ons"
            value={`$${bookingData.additional_services_total!.toFixed(2)}`}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {renderClient()}
      {renderContacts()}
      {renderMiddle()}
      {renderService()}
      {renderPackage()}
    </Box>
  );
};

export default BookingPreviewSidebar;
